/**
 * Crew personal ledger.
 *
 * One `CrewLedgerEntry` list is both the member's personal lines and the
 * company picture (rollup + ops snapshot). There is no second row type.
 *
 * Overnight hotel, meal, and ground post from `company.opsDesk.crewPay`
 * when a trip overnight is already clear (away field, stay sticks, crew
 * assigned on that stay). A rate of 0 means unset and is skipped — no $0
 * line. The same stay (owning mission × crew × field × category) posts once.
 * Removing an auto line voids that id so a later sync does not put it back.
 *
 * Amounts are company costs: negative. A typed pay amount is stored negative.
 */
import type { TripAssignment } from "./assignment";
import { icaoKey } from "./curfew";
import { hotelMissionForStay } from "./hotelOvernight";
import { hydrateCrewLedger, hydrateCrewLedgerEntry, type CrewLedgerCategory, type CrewLedgerEntry, type OpsMoneyCode } from "./opsLedger";
import { hydrateOpsDeskSettings } from "./opsDeskSettings";
import { overnightSticksAtField } from "./parkedOvernight";
import type { CharterRequest, CompanyProfile, CrewMember, Mission, OpsCrewPayRates } from "./types";

export const CREW_LEDGER_CAP = 400;

const OVERNIGHT_CATEGORIES = ["hotel", "meal", "ground"] as const;
type OvernightCategory = (typeof OVERNIGHT_CATEGORIES)[number];

const RATE_KEY: Record<OvernightCategory, keyof OpsCrewPayRates> = {
  hotel: "overnightHotel",
  meal: "overnightMeal",
  ground: "overnightGround",
};

export type CrewLedgerLineInput = {
  crewId: string;
  category?: CrewLedgerCategory;
  /** Positive magnitude or an already-negative cost. Zero is unset and is not posted. */
  amount: number;
  currency?: OpsMoneyCode;
  missionId?: string;
  flightNumber?: string;
  memo?: string;
  id?: string;
  createdAtUtc?: string;
};

export type CrewLedgerRollup = {
  count: number;
  total: number;
  byCategory: Record<CrewLedgerCategory, number>;
  byCrew: { crewId: string; total: number; count: number }[];
};

function text(raw: unknown, max: number): string {
  return String(raw ?? "").trim().slice(0, max);
}

export function opsMoneyCode(raw: unknown): OpsMoneyCode {
  const code = text(raw, 3).toUpperCase();
  return code === "USD" || code === "EUR" || code === "GBP" || code === "CAD" ? code : "CAD";
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/** Stable id for one overnight category on one stay. Fits the 64-char hydrate cap. */
export function crewOvernightEntryId(
  missionId: string,
  crewId: string,
  category: OvernightCategory,
  icao: string,
): string {
  const raw = `ovn:${missionId}:${crewId}:${category}:${icao}`;
  if (raw.length <= 64) return raw;
  return `ovn:${fnv1a(raw)}:${category}:${icao}`.slice(0, 64);
}

export function isCrewOvernightEntryId(id: string): boolean {
  return id.startsWith("ovn:");
}

/** Company-cost sign. Zero / non-finite is unset and is not a line. */
export function crewCostAmount(raw: unknown): number | null {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  const signed = n > 0 ? -n : n;
  return Math.round(signed * 100) / 100;
}

function capped(rows: CrewLedgerEntry[]): CrewLedgerEntry[] {
  return rows.slice(0, CREW_LEDGER_CAP);
}

export function hydrateCrewLedgerVoids(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const id = text(row, 64);
    if (!isCrewOvernightEntryId(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= CREW_LEDGER_CAP) break;
  }
  return out;
}

export function entriesForCrew(ledger: readonly CrewLedgerEntry[] | null | undefined, crewId: string): CrewLedgerEntry[] {
  const id = text(crewId, 64);
  if (!id) return [];
  return hydrateCrewLedger(ledger).filter((row) => row.crewId === id);
}

export function summarizeCrewLedger(ledger: readonly CrewLedgerEntry[] | null | undefined): CrewLedgerRollup {
  const rows = hydrateCrewLedger(ledger);
  const byCategory: Record<CrewLedgerCategory, number> = {
    pay: 0,
    hotel: 0,
    meal: 0,
    ground: 0,
    other: 0,
  };
  const crew = new Map<string, { crewId: string; total: number; count: number }>();
  let total = 0;
  for (const row of rows) {
    byCategory[row.category] = Math.round((byCategory[row.category] + row.amount) * 100) / 100;
    total = Math.round((total + row.amount) * 100) / 100;
    const hit = crew.get(row.crewId) ?? { crewId: row.crewId, total: 0, count: 0 };
    hit.total = Math.round((hit.total + row.amount) * 100) / 100;
    hit.count += 1;
    crew.set(row.crewId, hit);
  }
  return {
    count: rows.length,
    total,
    byCategory,
    byCrew: [...crew.values()].sort((a, b) => a.crewId.localeCompare(b.crewId)),
  };
}

export function appendCrewLedgerEntry(
  ledger: readonly CrewLedgerEntry[] | null | undefined,
  input: CrewLedgerLineInput,
  now = new Date(),
): { ledger: CrewLedgerEntry[]; entry: CrewLedgerEntry | null; error?: string } {
  const current = hydrateCrewLedger(ledger);
  const crewId = text(input.crewId, 64);
  if (!crewId) return { ledger: current, entry: null, error: "Crew is required." };
  const amount = crewCostAmount(input.amount);
  if (amount == null) return { ledger: current, entry: null, error: "Amount must be non-zero. Zero means unset." };
  const createdAtUtc = text(input.createdAtUtc, 40) || now.toISOString();
  const id = text(input.id, 64) || `crew-${crewId}-${now.getTime().toString(36)}`.slice(0, 64);
  if (current.some((row) => row.id === id)) return { ledger: current, entry: current.find((row) => row.id === id) ?? null };
  const entry = hydrateCrewLedgerEntry({
    id,
    crewId,
    category: input.category ?? "pay",
    amount,
    currency: opsMoneyCode(input.currency),
    createdAtUtc,
    missionId: text(input.missionId, 64),
    flightNumber: text(input.flightNumber, 16),
    memo: text(input.memo, 120),
  });
  if (!entry) return { ledger: current, entry: null, error: "Line could not be saved." };
  return { ledger: capped([entry, ...current]), entry };
}

export function patchCrewLedgerEntry(
  ledger: readonly CrewLedgerEntry[] | null | undefined,
  id: string,
  patch: { amount?: number; memo?: string },
): CrewLedgerEntry[] {
  const current = hydrateCrewLedger(ledger);
  const key = text(id, 64);
  return current.map((row) => {
    if (row.id !== key) return row;
    const amount = patch.amount === undefined ? row.amount : crewCostAmount(patch.amount);
    if (patch.amount !== undefined && amount == null) return row;
    const next = hydrateCrewLedgerEntry({
      ...row,
      amount: amount ?? row.amount,
      memo: patch.memo === undefined ? row.memo : text(patch.memo, 120),
    });
    return next ?? row;
  });
}

export function removeCrewLedgerEntry(
  ledger: readonly CrewLedgerEntry[] | null | undefined,
  voids: readonly string[] | null | undefined,
  id: string,
): { ledger: CrewLedgerEntry[]; voids: string[] } {
  const key = text(id, 64);
  const nextLedger = hydrateCrewLedger(ledger).filter((row) => row.id !== key);
  const nextVoids = hydrateCrewLedgerVoids(voids);
  if (isCrewOvernightEntryId(key) && !nextVoids.includes(key)) nextVoids.push(key);
  return { ledger: nextLedger, voids: nextVoids.slice(0, CREW_LEDGER_CAP) };
}

function sameField(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = icaoKey(a);
  const right = icaoKey(b);
  return Boolean(left) && left === right;
}

function assignmentCrewIds(
  missions: readonly Mission[],
  assignments: readonly TripAssignment[],
  owner: Mission,
  field: string,
): string[] {
  const stayMissions = new Set<string>();
  const stayRequests = new Set<string>();
  for (const mission of missions) {
    if (mission.status === "cancelled") continue;
    if (owner.aircraftId && mission.aircraftId && mission.aircraftId !== owner.aircraftId) continue;
    const onStay = mission.id === owner.id || sameField(mission.overnightAtIcao, field);
    if (!onStay) continue;
    stayMissions.add(mission.id);
    if (mission.inboxRequestId) stayRequests.add(mission.inboxRequestId);
  }
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const row of assignments) {
    if (row.superseded || row.status === "declined") continue;
    const crewId = text(row.crewId, 64);
    if (!crewId || seen.has(crewId)) continue;
    const linked =
      (row.missionId && stayMissions.has(row.missionId)) ||
      (row.ferryMissionId && stayMissions.has(row.ferryMissionId)) ||
      (row.returnFerryMissionId && stayMissions.has(row.returnFerryMissionId)) ||
      (row.requestId && stayRequests.has(row.requestId));
    if (!linked) continue;
    seen.add(crewId);
    ids.push(crewId);
  }
  return ids;
}

function overnightMemo(category: OvernightCategory, field: string, flightNumber: string): string {
  const label = category === "hotel" ? "hotel" : category === "meal" ? "meal" : "ground";
  const flight = flightNumber ? ` · ${flightNumber}` : "";
  return `Overnight ${label} ${field}${flight}`.slice(0, 120);
}

export type OvernightPostInput = {
  ledger?: readonly CrewLedgerEntry[] | null;
  voids?: readonly string[] | null;
  missions?: readonly Mission[] | null;
  assignments?: readonly TripAssignment[] | null;
  crew?: readonly Pick<CrewMember, "id" | "homeBaseIcao">[] | null;
  company?: Partial<CompanyProfile> | null;
  inbox?: readonly CharterRequest[] | null;
  currency?: unknown;
  now?: Date;
};

/**
 * Append missing overnight lines from ops-desk rates.
 * Returns the same logical lines when the stay was already posted.
 */
export function postCrewOvernightLines(input: OvernightPostInput): { ledger: CrewLedgerEntry[]; posted: number } {
  const current = hydrateCrewLedger(input.ledger);
  const voids = new Set(hydrateCrewLedgerVoids(input.voids));
  const rates = hydrateOpsDeskSettings(input.company?.opsDesk).crewPay;
  const currency = opsMoneyCode(input.currency);
  const now = input.now ?? new Date();
  const missions = input.missions ?? [];
  const assignments = input.assignments ?? [];
  const crew = input.crew ?? [];
  const roster = new Map(crew.map((member) => [member.id, member]));
  const home = icaoKey(input.company?.homeIcao);
  const seenStays = new Set<string>();
  const existing = new Set(current.map((row) => row.id));
  const added: CrewLedgerEntry[] = [];

  for (const mission of missions) {
    if (mission.status === "cancelled") continue;
    const field = icaoKey(mission.overnightAtIcao);
    if (!field || (home && field === home)) continue;
    const stayKey = `${mission.aircraftId || mission.registration}:${field}`;
    if (seenStays.has(stayKey)) continue;
    if (
      !overnightSticksAtField({
        awayIcao: field,
        homeIcao: home || input.company?.homeIcao || "",
        aircraftId: mission.aircraftId,
        missions,
        inbox: input.inbox ?? [],
        now,
      })
    ) {
      continue;
    }
    const owner = hotelMissionForStay(missions, field, mission.aircraftId);
    if (!owner) continue;
    seenStays.add(stayKey);
    const flightNumber = text(owner.flightNumber || owner.callsign, 16);
    for (const crewId of assignmentCrewIds(missions, assignments, owner, field)) {
      const member = roster.get(crewId);
      if (!member) continue;
      if (sameField(member.homeBaseIcao, field)) continue;
      for (const category of OVERNIGHT_CATEGORIES) {
        const rate = rates[RATE_KEY[category]];
        if (!(rate > 0)) continue;
        const id = crewOvernightEntryId(owner.id, crewId, category, field);
        if (existing.has(id) || voids.has(id)) continue;
        const entry = hydrateCrewLedgerEntry({
          id,
          crewId,
          category,
          amount: -rate,
          currency,
          createdAtUtc: now.toISOString(),
          missionId: owner.id,
          flightNumber,
          memo: overnightMemo(category, field, flightNumber),
        });
        if (!entry) continue;
        existing.add(id);
        added.push(entry);
      }
    }
  }

  if (!added.length) return { ledger: current, posted: 0 };
  return { ledger: capped([...added, ...current]), posted: added.length };
}
